import React, { useState } from 'react';

function ComparablesTable({ data, onAddToWatchlist, watchedStocks }) {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return '--';
    return `$${Number(value).toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (value == null || isNaN(value)) return '--';
    const num = Number(value) * 100; // Convert decimal to percentage
    const color = num >= 0 ? 'text-green-400' : 'text-red-400';
    return <span className={color}>{num > 0 ? '+' : ''}{num.toFixed(2)}%</span>;
  };

  const formatNumber = (value, decimals = 2) => {
    if (value == null || isNaN(value)) return '--';
    return Number(value).toFixed(decimals);
  };

  const formatMarketCap = (value) => {
    if (value == null || isNaN(value)) return '--';
    const num = Number(value);
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toLocaleString()}`;
  };

  const handleSort = (field) => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
  };

  const isWatched = (ticker) => {
    return watchedStocks?.some(stock => stock.ticker === ticker) || false;
  };

  const SortableHeader = ({ field, label, multiLine = false }) => (
    <th
      className="text-right py-2 px-2 cursor-pointer hover:opacity-80"
      style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
      onClick={() => handleSort(field)}
    >
      {multiLine ? (
        <span dangerouslySetInnerHTML={{ __html: label.replace('\n', '<br/>') }} />
      ) : (
        label
      )}{' '}
      {sortField === field && (sortDirection === 'asc' ? '↑' : '↓')}
    </th>
  );

  // Early return if no data - must be before sortedData calculation
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No comparables data available</p>
      </div>
    );
  }

  const sortedData = [...data].sort((a, b) => {
    if (!sortField) return 0;

    const aVal = a[sortField];
    const bVal = b[sortField];

    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    const comparison = aVal > bVal ? 1 : -1;
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs min-w-max">
        <thead>
          <tr className="border-b border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>
            {/* Ticker */}
            <th className="text-left py-2 px-2 sticky left-0 z-10" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>
              Ticker
            </th>
            <SortableHeader field="Current Price" label="Current Price" />

            {/* Highs */}
            <th className="text-center py-2 px-2" style={{ backgroundColor: '#1a1a1a', color: '#888888', borderLeft: '1px solid #333' }} colSpan="6">
              HIGHS
            </th>

            {/* Lows */}
            <th className="text-center py-2 px-2" style={{ backgroundColor: '#1a1a1a', color: '#888888', borderLeft: '1px solid #333' }} colSpan="6">
              LOWS
            </th>

            {/* P/E Metrics */}
            <th className="text-center py-2 px-2" style={{ backgroundColor: '#1a1a1a', color: '#888888', borderLeft: '1px solid #333' }} colSpan="4">
              P/E METRICS
            </th>

            {/* Technicals */}
            <th className="text-center py-2 px-2" style={{ backgroundColor: '#1a1a1a', color: '#888888', borderLeft: '1px solid #333' }} colSpan="3">
              TECHNICALS
            </th>

            {/* Market Cap */}
            <th className="text-center py-2 px-2" style={{ backgroundColor: '#1a1a1a', color: '#888888', borderLeft: '1px solid #333' }}>
              VALUATION
            </th>

            {/* Actions */}
            <th className="text-center py-2 px-2 sticky right-0 z-10" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>
              Actions
            </th>
          </tr>
          <tr className="border-b border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>
            {/* Ticker */}
            <th className="text-left py-2 px-2 sticky left-0 z-10" style={{ backgroundColor: '#1a1a1a' }}></th>
            <th className="text-right py-2 px-2" style={{ backgroundColor: '#1a1a1a' }}></th>

            {/* Highs */}
            <SortableHeader field="ATH" label="ATH" />
            <SortableHeader field="ATH %Chg" label="ATH %Chg" />
            <SortableHeader field="6 Mo High" label="6 Mo High" />
            <SortableHeader field="6 Mo High %Chg" label="6 Mo %Chg" />
            <SortableHeader field="5 Yr High" label="5 Yr High" />
            <SortableHeader field="5 Yr High %Chg" label="5 Yr %Chg" />

            {/* Lows */}
            <SortableHeader field="ATL" label="ATL" />
            <SortableHeader field="ATL %Chg" label="ATL %Chg" />
            <SortableHeader field="6 Mo Low" label="6 Mo Low" />
            <SortableHeader field="6 Mo Low %Chg" label="6 Mo %Chg" />
            <SortableHeader field="5 Yr Low" label="5 Yr Low" />
            <SortableHeader field="5 Yr Low %Chg" label="5 Yr %Chg" />

            {/* P/E Metrics */}
            <SortableHeader field="Current PE" label="Current" />
            <SortableHeader field="5 Yr Avg PE" label="5Y Avg" />
            <SortableHeader field="5 Yr Low PE" label="5Y Low" />
            <SortableHeader field="5 Yr High PE" label="5Y High" />

            {/* Technicals */}
            <SortableHeader field="MACD" label="MACD" />
            <SortableHeader field="MACD Signal" label="Signal" />
            <SortableHeader field="RSI" label="RSI" />

            {/* Market Cap */}
            <SortableHeader field="Market Cap" label="Market Cap" />

            {/* Actions */}
            <th className="text-center py-2 px-2 sticky right-0 z-10" style={{ backgroundColor: '#1a1a1a' }}></th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, index) => (
            <tr key={index} className="border-b border-gray-800 hover:bg-gray-700/30">
              {/* Ticker */}
              <td className="py-2 px-2 font-medium text-white sticky left-0 bg-gray-800">
                {isWatched(row.Ticker) && <span className="mr-1">⭐</span>}
                {row.Ticker}
              </td>
              <td className="py-2 px-2 text-right text-white">
                {formatCurrency(row['Current Price'])}
              </td>

              {/* Highs */}
              <td className="py-2 px-2 text-right text-white" style={{ borderLeft: '1px solid #333' }}>
                {formatCurrency(row['ATH'])}
              </td>
              <td className="py-2 px-2 text-right">
                {formatPercent(row['ATH %Chg'])}
              </td>
              <td className="py-2 px-2 text-right text-white">
                {formatCurrency(row['6 Mo High'])}
              </td>
              <td className="py-2 px-2 text-right">
                {formatPercent(row['6 Mo High %Chg'])}
              </td>
              <td className="py-2 px-2 text-right text-white">
                {formatCurrency(row['5 Yr High'])}
              </td>
              <td className="py-2 px-2 text-right">
                {formatPercent(row['5 Yr High %Chg'])}
              </td>

              {/* Lows */}
              <td className="py-2 px-2 text-right text-white" style={{ borderLeft: '1px solid #333' }}>
                {formatCurrency(row['ATL'])}
              </td>
              <td className="py-2 px-2 text-right">
                {formatPercent(row['ATL %Chg'])}
              </td>
              <td className="py-2 px-2 text-right text-white">
                {formatCurrency(row['6 Mo Low'])}
              </td>
              <td className="py-2 px-2 text-right">
                {formatPercent(row['6 Mo Low %Chg'])}
              </td>
              <td className="py-2 px-2 text-right text-white">
                {formatCurrency(row['5 Yr Low'])}
              </td>
              <td className="py-2 px-2 text-right">
                {formatPercent(row['5 Yr Low %Chg'])}
              </td>

              {/* P/E Metrics */}
              <td className="py-2 px-2 text-right text-white" style={{ borderLeft: '1px solid #333' }}>
                {formatNumber(row['Current PE'])}
              </td>
              <td className="py-2 px-2 text-right text-white">
                {formatNumber(row['5 Yr Avg PE'])}
              </td>
              <td className="py-2 px-2 text-right text-white">
                {formatNumber(row['5 Yr Low PE'])}
              </td>
              <td className="py-2 px-2 text-right text-white">
                {formatNumber(row['5 Yr High PE'])}
              </td>

              {/* Technicals */}
              <td className="py-2 px-2 text-right text-white" style={{ borderLeft: '1px solid #333' }}>
                {formatNumber(row['MACD'])}
              </td>
              <td className="py-2 px-2 text-right text-white">
                {formatNumber(row['MACD Signal'])}
              </td>
              <td className="py-2 px-2 text-right text-white">
                {formatNumber(row['RSI'])}
              </td>

              {/* Market Cap */}
              <td className="py-2 px-2 text-right text-white" style={{ borderLeft: '1px solid #333' }}>
                {formatMarketCap(row['Market Cap'])}
              </td>

              {/* Actions */}
              <td className="py-2 px-2 text-center sticky right-0 bg-gray-800">
                {!isWatched(row.Ticker) && (
                  <button
                    onClick={() => onAddToWatchlist(row.Ticker)}
                    className="text-blue-400 hover:text-blue-300 text-xs px-2 py-1 rounded border border-blue-400 hover:border-blue-300"
                  >
                    Watch
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 text-sm text-gray-400">
        Showing {sortedData.length} stocks analyzed
      </div>
    </div>
  );
}

export default ComparablesTable;
